import React, {
  useRef,
  useEffect,
} from 'lib/react';

import { EVGrid, EVInputLabel, EVButton } from 'lib/components';
import { useTranslation } from 'lib/translation';
import PropTypes from 'prop-types';

import { COUNTRY_LIST, COUNTRY_CA } from 'constants/AppConstants';
import TextInputField from 'commonComponents/textInputField';
import SelectField from 'commonComponents/select-field';
import MaskedPhoneNumber from 'commonComponents/maskedPhoneNumber';
import AddressInformationStyles from './AddressInformationStyles';
import {
  getStateList,
  validateEmail,
  validateText,
  validateAddress,
  validateCountry,
  validatePhoneNumber,
  validateState,
  validateZipCode,
  validateExt,
  isValidCybersourceCompanyName,
  getCybersourceCompanyNameInvalidKey,
} from '../../utils/validation.util';
import AddressInformationLoader from './components/AddressInformationLoader/AddressInformationLoader.component';

export default function AddressInformation({
  isLoading,
  addressInfo,
  addressInfoErrors,
  handleChange,
  handleErrorChange,
  onCancel,
  updateElementRefs,
  // #genai Phase 1 locks Company/Country; Phase 2 add-payment forms pass false to make them
  // editable (Figma 269:20728 / 269:21301).
  disableCompanyCountry,
  // #genai Phase 2 Add Credit Card / Add Bank (ACH) forms: "Company name" label +
  // "Company name is required" copy, and mark Address/City/State/Zip mandatory (asterisk +
  // inline required error) per Figma 269:20728 / 269:21301.
  isPhase2AddPayment,
}) {
  if (isLoading) {
    return (
      <AddressInformationLoader />
    );
  }
  const classes = AddressInformationStyles();
  const { t } = useTranslation();

  // #genai Phase 2 Add Payment only: Canada shows Province / Postal code (not State / Zip code).
  // The message copy is resolved per country rather than from render state so it still matches the
  // label while the user is switching country and the dependent fields are re-validated.
  const isCanadaPhase2For = (country) => isPhase2AddPayment && country === COUNTRY_CA;
  const stateRequiredFor = (country) => t(isCanadaPhase2For(country)
    ? 'address_information__province_required'
    : 'address_information__state_required');
  const zipRequiredFor = (country) => t(isCanadaPhase2For(country)
    ? 'address_information__postal_code_required'
    : 'address_information__zip_required');
  // #genai Phase 2 invalid UX copy for email / Zip / Postal (screenshot). Other fields keep
  // generic "This field is incorrect" inline; banner still gets the specific invalid key.
  const zipInvalidFor = (country) => t(isCanadaPhase2For(country)
    ? 'address_information__invalid_postal_code'
    : 'address_information__invalid_zip');

  const isCanadaPhase2 = isCanadaPhase2For(addressInfo.country);
  const stateLabel = isCanadaPhase2
    ? t('address_information__province')
    : t('address_information__state');
  const zipLabel = isCanadaPhase2
    ? t('address_information__postal_code')
    : t('address_information__zip');
  const stateRequiredBanner = stateRequiredFor(addressInfo.country);
  const zipRequiredBanner = zipRequiredFor(addressInfo.country);
  const zipInvalidMsg = zipInvalidFor(addressInfo.country);
  const emailInvalidMsg = t('address_information__invalid_email');

  const validateFieldAndHandleChange = (
    fieldId,
    { target: { id, value } },
    validator,
    requiredFieldMessage,
    invalidFieldMessage,
    isRequired = true,
    // #genai Inline helper text for an invalid value (email / zip / company on Phase 2). Empty
    // falls back to the generic copy; the banner line always uses invalidFieldMessage, so the
    // company field can name the exact rule inline while the banner stays one short sentence.
    specificInlineMessage = '',
  ) => {
    const targetId = fieldId || id;
    handleChange(targetId, value);
    // #genai Phase 2 company is validated after trim(), so spaces-only must use the required path
    // (same as submit). Other fields keep a strict '' check so their invalid-format copy is unchanged.
    const isBlankRequiredValue = value === ''
      || (isPhase2AddPayment && targetId === 'company' && !value.trim());
    if (isBlankRequiredValue) {
      if (isRequired) {
        handleErrorChange(targetId, t('address_information__field_required'), requiredFieldMessage);
      } else {
        handleErrorChange(targetId, '', '');
      }
    } else if (validator()) {
      handleErrorChange(targetId, '', '');
    } else {
      const inlineInvalid = specificInlineMessage || t('address_information__invalid_field');
      handleErrorChange(targetId, inlineInvalid, invalidFieldMessage);
    }
  };

  const elementRefInfo = ['emailAddress', 'company', 'country', 'phone', 'phoneExt', 'secondaryPhone', 'secondaryExt', 'address1', 'city', 'state', 'zip']
    .reduce((refInfo, id) => ({ ...refInfo, [id]: { id, elementRef: useRef(null), message: '' } }), {});

  // #genai Phase 2 add-payment field-error UX (Figma 269:21301 / 269:20728): labels stay their
  // default charcoal color even on error — only the input box border/fill + helper text turn red.
  // Phase 1 flows (isPhase2AddPayment=false) retain the legacy behavior where the label goes red
  // too, so the shared component's callers outside Phase 2 are unaffected.
  const labelErrorFor = (fieldError) => !isPhase2AddPayment && fieldError.length > 0;

  useEffect(() => {
    if (updateElementRefs === null) return;
    updateElementRefs(elementRefInfo);
  }, []);

  const emailField = (
    <EVGrid className={classes.fieldContainer} item>
      <TextInputField
        inputId="emailAddress"
        inputValue={addressInfo.emailAddress}
        size="small"
        handleChange={(event) => validateFieldAndHandleChange(
          'emailAddress',
          event,
          () => validateEmail(event.target.value),
          t('address_information__email_required'),
          emailInvalidMsg,
          true,
          // #genai Phase 2: show screenshot invalid email copy inline (not generic incorrect).
          isPhase2AddPayment ? emailInvalidMsg : '',
        )}
        inputLabelProps={{
          className: classes.label,
          classes: { asterisk: classes.asterisk },
          required: true,
          error: labelErrorFor(addressInfoErrors.emailAddress),
        }}
        inputFieldProps={{
          fullWidth: true,
          inputProps: { maxLength: 400 },
          classes: { root: classes.addressInputText },
          FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
          inputRef: elementRefInfo.emailAddress.elementRef,
        }}
        labelText={t('address_information__email')}
        helperText={addressInfoErrors.emailAddress}
      />
    </EVGrid>
  );

  const companyField = (
    <EVGrid className={classes.fieldContainer} item>
      <TextInputField
        inputId="company"
        inputValue={addressInfo.company}
        size="small"
        handleChange={(event) => validateFieldAndHandleChange(
          'company',
          event,
          () => (isPhase2AddPayment
            ? isValidCybersourceCompanyName(event.target.value)
            : validateText(event.target.value)),
          t(isPhase2AddPayment ? 'address_information__company_name_required' : 'address_information__company_required'),
          // #genai Banner line stays one short sentence on Phase 2; Phase 1 keeps its legacy copy.
          t(isPhase2AddPayment ? 'address_information__company_name_invalid' : 'address_information__invalid_company'),
          true,
          // #genai Inline helper names the rule that failed (first character / characters / PII).
          isPhase2AddPayment ? t(getCybersourceCompanyNameInvalidKey(event.target.value)) : '',
        )}
        inputLabelProps={{
          className: classes.label,
          classes: { asterisk: classes.asterisk },
          required: true,
          error: labelErrorFor(addressInfoErrors.company),
        }}
        inputFieldProps={{
          fullWidth: true,
          inputProps: { maxLength: 166 },
          classes: { root: classes.addressInputText },
          FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
          inputRef: elementRefInfo.company.elementRef,
          disabled: disableCompanyCountry,
        }}
        labelText={t(isPhase2AddPayment ? 'address_information__company_name' : 'address_information__company')}
        helperText={addressInfoErrors.company}
      />
    </EVGrid>
  );

  return (
    <EVGrid container item direction="column">
      {/* #genai Phase 2 Add Payment shows Company name above Billing email (Figma 269:20728).
          Ordering the DOM itself — instead of swapping the two with CSS `order` on the page — keeps
          tab navigation matching the visual order. Phase 1 keeps its original email-first order. */}
      {isPhase2AddPayment ? companyField : emailField}
      {isPhase2AddPayment ? emailField : companyField}
      <EVGrid className={classes.fieldContainer} item>
        <EVInputLabel
          className={classes.label}
          classes={{ asterisk: classes.asterisk }}
          required
          error={labelErrorFor(addressInfoErrors.country)}
        >
          {t('address_information__country')}
        </EVInputLabel>
        <SelectField
          selectId="country"
          variant="outlined"
          fullWidth
          selectedValue={addressInfo.country}
          handleChange={(event) => {
            const nextCountry = event.target.value;
            validateFieldAndHandleChange(
              'country',
              event,
              () => validateCountry(nextCountry),
              t('address_information__country_required'),
              t('address_information__invalid_country'),
            );
            // #genai The state list depends on country, so the previous selection no longer applies.
            // Phase 2 clears it (as ContactAddressInformation does) so the message matches the new
            // State / Province label; Phase 1 keeps its legacy re-validation with the country event.
            validateFieldAndHandleChange(
              'state',
              isPhase2AddPayment ? { target: { id: 'state', value: '' } } : event,
              () => validateState(nextCountry, ''),
              stateRequiredFor(nextCountry),
              t('address_information__invalid_state'),
            );
            // #genai Zip format and copy both depend on country, so refresh the field against the
            // new one — otherwise the label reads "Postal code" while the error still names Zip.
            // "Required" is limited to a Zip that already errored so picking a country never
            // errors an untouched field.
            if (isPhase2AddPayment) {
              const nextZipInvalidMsg = zipInvalidFor(nextCountry);
              validateFieldAndHandleChange(
                'zip',
                { target: { id: 'zip', value: addressInfo.zip } },
                () => validateZipCode(nextCountry, addressInfo.zip),
                zipRequiredFor(nextCountry),
                nextZipInvalidMsg,
                addressInfoErrors.zip !== '',
                nextZipInvalidMsg,
              );
            }
          }}
          // #genai Explicit error class keeps the Country outline on #BC1616.
          className={addressInfoErrors.country.length > 0
            ? `${classes.countryOptions} ${classes.selectErrorOutline}`
            : classes.countryOptions}
          selectOptions={COUNTRY_LIST}
          error={addressInfoErrors.country.length > 0}
          errorMessage={addressInfoErrors.country}
          // #genai Match Country select helper text to the Phase 2 error outline (#BC1616).
          errorProps={{ className: classes.addressInputHelperText }}
          innerRef={elementRefInfo.country.elementRef}
          disabled={disableCompanyCountry}
        />
      </EVGrid>
      <EVGrid container item className={classes.phoneContainer} direction="row">
        <EVGrid className={classes.fieldContainer} item>
          <TextInputField
            inputId="phone"
            inputValue={addressInfo.phone}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'phone',
              event,
              () => validatePhoneNumber(event.target.value),
              t('address_information__phone_required'),
              t('address_information__invalid_phone'),
              false,
            )}
            inputLabelProps={{
              className: classes.label,
              classes: { asterisk: classes.asterisk },
              error: labelErrorFor(addressInfoErrors.phone),
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 12 },
              classes: { root: classes.phoneInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
              inputRef: elementRefInfo.phone.elementRef,
            }}
            labelText={t(isPhase2AddPayment ? 'address_information__primary_phone' : 'address_information__phone')}
            helperText={addressInfoErrors.phone}
            InputProps={{
              inputComponent: MaskedPhoneNumber,
            }}
          />
        </EVGrid>
        <EVGrid className={classes.fieldContainer} item>
          <TextInputField
            inputId="phoneExt"
            inputValue={addressInfo.phoneExt}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'phoneExt',
              event,
              () => validateExt(event.target.value),
              t('address_information__phone_ext_required'),
              t('address_information__invalid_phone_ext'),
              false,
            )}
            inputLabelProps={{
              className: classes.label,
              classes: { asterisk: classes.asterisk },
              error: labelErrorFor(addressInfoErrors.phoneExt),
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 5 },
              classes: { root: classes.phoneExtInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
              inputRef: elementRefInfo.phoneExt.elementRef,
            }}
            labelText={t('address_information__phone_ext')}
            helperText={addressInfoErrors.phoneExt}
          />
        </EVGrid>
      </EVGrid>
      <EVGrid container item className={classes.secondaryPhoneContainer} direction="row">
        <EVGrid className={classes.fieldContainer} item>
          <TextInputField
            inputId="secondaryPhone"
            inputValue={addressInfo.secondaryPhone}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'secondaryPhone',
              event,
              () => validatePhoneNumber(event.target.value),
              t('address_information__secondary_phone_required'),
              t('address_information__invalid_secondary_phone'),
              false,
            )}
            inputLabelProps={{
              className: classes.label,
              error: labelErrorFor(addressInfoErrors.secondaryPhone),
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 12 },
              classes: { root: classes.secondaryPhoneInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
              inputRef: elementRefInfo.secondaryPhone.elementRef,
            }}
            labelText={t('address_information__secondary_phone')}
            helperText={addressInfoErrors.secondaryPhone}
            InputProps={{
              inputComponent: MaskedPhoneNumber,
            }}
          />
        </EVGrid>
        <EVGrid className={classes.fieldContainer} item>
          <TextInputField
            inputId="secondaryExt"
            inputValue={addressInfo.secondaryExt}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'secondaryExt',
              event,
              () => validateExt(event.target.value),
              t('address_information__secondary_ext_required'),
              t('address_information__invalid_secondary_ext'),
              false,
            )}
            inputLabelProps={{
              className: classes.label,
              classes: { asterisk: classes.asterisk },
              error: labelErrorFor(addressInfoErrors.secondaryExt),
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 5 },
              classes: { root: classes.phoneExtInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
              inputRef: elementRefInfo.secondaryExt.elementRef,
            }}
            labelText={t('address_information__phone_ext')}
            helperText={addressInfoErrors.secondaryExt}
          />
        </EVGrid>
      </EVGrid>
      <EVGrid className={classes.fieldContainer} item>
        <TextInputField
          inputId="address1"
          inputValue={addressInfo.address1}
          size="small"
          handleChange={(event) => validateFieldAndHandleChange(
            'address1',
            event,
            () => validateAddress(event.target.value),
            t('address_information__address1_required'),
            t('address_information__invalid_address1'),
            isPhase2AddPayment,
          )}
          inputLabelProps={{
            className: classes.label,
            classes: { asterisk: classes.asterisk },
            required: isPhase2AddPayment,
            error: labelErrorFor(addressInfoErrors.address1),
          }}
          inputFieldProps={{
            fullWidth: true,
            inputProps: { maxLength: 300 },
            classes: { root: classes.addressInputText },
            FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
            inputRef: elementRefInfo.address1.elementRef,
          }}
          labelText={t('address_information__address1')}
          helperText={addressInfoErrors.address1}
        />
      </EVGrid>
      <EVGrid container item direction="row" className={classes.cityStateContainer}>
        <EVGrid className={classes.cityContainer} item>
          <TextInputField
            inputId="city"
            inputValue={addressInfo.city}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'city',
              event,
              () => validateText(event.target.value),
              t('address_information__city_required'),
              t('address_information__invalid_city'),
              isPhase2AddPayment,
            )}
            inputLabelProps={{
              className: classes.label,
              classes: { asterisk: classes.asterisk },
              required: isPhase2AddPayment,
              error: labelErrorFor(addressInfoErrors.city),
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 80 },
              classes: { root: classes.addressInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
              inputRef: elementRefInfo.city.elementRef,
            }}
            labelText={t('address_information__city')}
            helperText={addressInfoErrors.city}
          />
        </EVGrid>
        <EVGrid className={classes.stateContainer} item>
          <EVInputLabel
            className={classes.label}
            classes={{ asterisk: classes.asterisk }}
            required={isPhase2AddPayment}
            error={labelErrorFor(addressInfoErrors.state)}
          >
            {stateLabel}

          </EVInputLabel>
          <SelectField
            selectId="state"
            variant="outlined"
            fullWidth
            selectedValue={addressInfo.state}
            handleChange={
              (event) => validateFieldAndHandleChange(
                'state',
                event,
                () => validateState(addressInfo.country, event.target.value),
                stateRequiredBanner,
                t('address_information__invalid_state'),
              )
            }
            // #genai Error class is independent of MUI disabled state, so an empty-country
            // State/Province select still receives the #BC1616 outline after submit.
            className={addressInfoErrors.state.length > 0
              ? `${classes.stateOptions} ${classes.selectErrorOutline}`
              : classes.stateOptions}
            selectOptions={getStateList(addressInfo.country)}
            error={addressInfoErrors.state.length > 0}
            errorMessage={addressInfoErrors.state}
            // #genai Match State/Province select helper text to the Phase 2 error outline (#BC1616).
            errorProps={{ className: classes.addressInputHelperText }}
            innerRef={elementRefInfo.state.elementRef}
            // #genai Phase 2 add-payment UX ONLY: States depend on Country, so keep State
            // disabled until a Country is picked so a fresh (empty-Country) form never opens an
            // empty State menu. On Phase 1 billing (BillingInformation) Country is locked via
            // disableCompanyCountry — an empty-Country record there would ALSO lock State,
            // deadlocking the user. Gating on isPhase2AddPayment scopes the guardrail to the
            // Add-Bank / Add-Card forms it was designed for and restores pre-Phase-2 behaviour
            // (State always enabled) for every other consumer of AddressInformation.
            disabled={isPhase2AddPayment && !addressInfo.country}
          />
        </EVGrid>
        <EVGrid className={classes.zipContainer} item>
          <TextInputField
            inputId="zip"
            inputValue={addressInfo.zip}
            size="small"
            handleChange={(event) => validateFieldAndHandleChange(
              'zip',
              event,
              () => validateZipCode(addressInfo.country, event.target.value),
              zipRequiredBanner,
              zipInvalidMsg,
              isPhase2AddPayment,
              // #genai Phase 2: Zip / Postal invalid uses screenshot UX copy inline.
              isPhase2AddPayment ? zipInvalidMsg : '',
            )}
            inputLabelProps={{
              className: classes.label,
              classes: { asterisk: classes.asterisk },
              required: isPhase2AddPayment,
              error: labelErrorFor(addressInfoErrors.zip),
            }}
            inputFieldProps={{
              fullWidth: true,
              inputProps: { maxLength: 80 },
              classes: { root: classes.addressInputText },
              FormHelperTextProps: { classes: { error: classes.addressInputHelperText } },
              inputRef: elementRefInfo.zip.elementRef,
            }}
            labelText={zipLabel}
            helperText={addressInfoErrors.zip}
          />
        </EVGrid>
      </EVGrid>
      <EVGrid container wrap="nowrap" className={classes.buttonContainer}>
        <EVButton
          type="submit"
          id="submitOrderBtn"
          variant="contained"
          style={{ backgroundColor: '#007CAD', color: '#FFFFFF' }}
          className={classes.button}
        >
          {t('common__save')}
        </EVButton>
        <EVButton data-testid="cancelButton" onClick={onCancel} variant="outlined" color="primary" className={classes.button}>
          {t('address_information__cancel')}
        </EVButton>
      </EVGrid>
    </EVGrid>
  );
}

AddressInformation.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  addressInfo: PropTypes.shape().isRequired,
  addressInfoErrors: PropTypes.shape().isRequired,
  handleChange: PropTypes.func.isRequired,
  handleErrorChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  updateElementRefs: PropTypes.func,
  disableCompanyCountry: PropTypes.bool, // #genai
  isPhase2AddPayment: PropTypes.bool, // #genai
};

AddressInformation.defaultProps = {
  updateElementRefs: null,
  disableCompanyCountry: true, // #genai Phase 1 default — Company/Country locked
  isPhase2AddPayment: false, // #genai Phase 1 default — "Company" label + touch-based address validation
};
